import { Layout } from '@community_dev/pixels'
import { Alert, Button, Card, Layout as AntLayout, Space, Typography } from 'antd'
import React, { useCallback, useEffect, useMemo, useReducer, type JSX } from 'react'
import { useHistory, useLocation } from 'react-router'

import { ActivityTable } from './ActivityTable'
import { HAIRLINE } from './colors'
import { FilterSidebar } from './FilterSidebar'
import { useActivities } from './queries'
import { FilterAction, filterReducer, initialFilterState } from './state'

import { ActivitiesParams } from 'api/activityLog'

const { Title, Text } = Typography
const { Sider, Content } = AntLayout

const PAGE_SIZE = 50

const CLIENT_ACTIONS: FilterAction['type'][] = ['set_clients', 'reset_clients', 'toggle_client']

export function Activity(): JSX.Element {
  const { search } = useLocation()
  const history = useHistory()
  const [state, dispatchFilter] = useReducer(filterReducer, search, (initialSearch: string) => ({
    ...initialFilterState,
    clientIds: new URLSearchParams(initialSearch).getAll('client_id'),
  }))

  useEffect(() => {
    dispatchFilter({ type: 'set_clients', ids: new URLSearchParams(search).getAll('client_id') })
  }, [search])

  // The URL holds the client filter, so a client action changes the URL and the effect above applies it.
  // FilterSidebar cancels its pending search when dispatch changes, so dispatch must keep one identity and read the URL from history.
  const dispatch = useCallback(
    (action: FilterAction) => {
      if (!CLIENT_ACTIONS.includes(action.type)) {
        dispatchFilter(action)
        return
      }
      const query = new URLSearchParams(history.location.search)
      const clientIds = filterReducer({ ...initialFilterState, clientIds: query.getAll('client_id') }, action).clientIds
      query.delete('client_id')
      clientIds.forEach((id) => query.append('client_id', id))
      history.replace({ search: query.toString() })
    },
    [history],
  )

  const params = useMemo<ActivitiesParams>(
    () => ({
      q: state.q || undefined,
      client_id: state.clientIds.length ? state.clientIds : undefined,
      actor_seat_id: state.actorIds.length ? state.actorIds : undefined,
      exclude_system_actors: state.excludeSystemActors || undefined,
      category: state.category || undefined,
      occurred_at_from: state.timeFrom || undefined,
      occurred_at_to: state.timeTo || undefined,
      cursor: state.cursor || undefined,
      limit: PAGE_SIZE,
    }),
    [state],
  )

  const { data, isFetching, error } = useActivities(params)
  const rows = data?.data || []
  const nextCursor = data?.nextCursor || null
  const total = data?.total

  const pageStart = state.prevCursors.length * PAGE_SIZE + (rows.length > 0 ? 1 : 0)
  const pageEnd = pageStart + Math.max(rows.length - (rows.length > 0 ? 1 : 0), 0)
  const totalLabel = total ? `${(total.count || 0).toLocaleString()}${total.capped ? '+' : ''}` : null

  return (
    <Layout height="100%" padding="12px">
      <Card
        bodyStyle={{ padding: 0 }}
        bordered
        title={
          <Layout padding="12px 0">
            <Title level={4} style={{ margin: 0 }}>
              Activity
            </Title>
            <Text type="secondary">
              Key event-based activities occurring in the platform. High-traffic events (fans, subscriptions, messages)
              are not included.
            </Text>
          </Layout>
        }
      >
        <AntLayout style={{ background: 'transparent', minHeight: 540 }}>
          <Sider style={{ background: 'transparent', borderRight: `1px solid ${HAIRLINE}` }} width={280}>
            <FilterSidebar dispatch={dispatch} state={state} />
          </Sider>
          <Content style={{ padding: 16 }}>
            {error ? <Alert message={`Could not load activity: ${(error as Error).message}`} type="error" /> : null}
            <ActivityTable dispatch={dispatch} loading={isFetching} rows={rows} />
            <div
              style={{
                alignItems: 'center',
                borderTop: `1px solid ${HAIRLINE}`,
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 8,
                paddingTop: 12,
              }}
            >
              <Text type="secondary">
                {rows.length === 0
                  ? totalLabel
                    ? `Showing 0 of ${totalLabel} rows`
                    : 'No activity for these filters.'
                  : totalLabel
                  ? `Showing ${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${totalLabel} rows`
                  : `Showing ${rows.length} row${rows.length === 1 ? '' : 's'}`}
              </Text>
              <Space>
                <Button
                  disabled={state.prevCursors.length === 0}
                  onClick={() => dispatch({ type: 'page_prev' })}
                  size="small"
                >
                  Prev
                </Button>
                <Button
                  disabled={!nextCursor}
                  onClick={() => dispatch({ type: 'page_next', nextCursor })}
                  size="small"
                  type="primary"
                >
                  Next
                </Button>
              </Space>
            </div>
          </Content>
        </AntLayout>
      </Card>
    </Layout>
  )
}
